import React, { useState, useEffect, useCallback } from 'react';
import { Phone, Mail, Calendar, CheckCircle, AlertTriangle, Pencil, User, Target, TrendingUp, Clock, ChevronRight, Car, Settings, Trophy, Crown, Medal, ShoppingCart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const INTERACTION_TYPE_LABELS = {
  contact_telephonique: 'Telephone',
  contact_physique: 'Physique',
  promotion_atelier: 'Promo atelier',
  cas_carrosserie: 'Carrosserie',
  vente_voiture: 'Vente voiture',
  proposition_offre: 'Offre',
};

const DAYS_FR = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MONTHS_FR = ['Janvier','Fevrier','Mars','Avril','Mai','Juin','Juillet','Aout','Septembre','Octobre','Novembre','Decembre'];

const RelancesPage = ({ auth, onLogout }) => {
  const { t } = useTranslation();
  const [relances, setRelances] = useState({ overdue: [], today: [], upcoming: [], stats: {} });
  const [employeeStats, setEmployeeStats] = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [salesObjectives, setSalesObjectives] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [showObjectiveDialog, setShowObjectiveDialog] = useState(false);
  const [editingObjective, setEditingObjective] = useState(null);
  const [objectiveValue, setObjectiveValue] = useState(50);
  const [showSalesObjDialog, setShowSalesObjDialog] = useState(false);
  const [editingSalesObj, setEditingSalesObj] = useState(null);
  const [salesObjForm, setSalesObjForm] = useState({ neuf_target: 0, occasion_target: 0 });
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const navigate = useNavigate();

  const loadData = useCallback(async () => {
    try {
      const [relRes, statsRes, objRes, lbRes, salesRes] = await Promise.all([
        axios.get(`${API}/clients/relances`),
        axios.get(`${API}/relance-stats`),
        axios.get(`${API}/relance-objectives`),
        axios.get(`${API}/relance-leaderboard`),
        axios.get(`${API}/sales-objectives?month=${selectedMonth}`),
      ]);
      setRelances(relRes.data);
      setEmployeeStats(statsRes.data);
      setObjectives(objRes.data);
      setLeaderboard(lbRes.data);
      setSalesObjectives(salesRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [selectedMonth]);

  useEffect(() => { loadData(); }, [loadData]);

  const openObjectiveEdit = (emp) => {
    setEditingObjective(emp);
    setObjectiveValue(emp.weekly_target || 50);
    setShowObjectiveDialog(true);
  };

  const saveObjective = async () => {
    if (!editingObjective) return;
    try {
      await axios.put(`${API}/relance-objectives/${editingObjective.employee_id}`, { weekly_target: objectiveValue });
      toast.success(`Objectif relances mis a jour: ${objectiveValue}/semaine`);
      setShowObjectiveDialog(false);
      loadData();
    } catch { toast.error('Erreur'); }
  };

  const openSalesObjEdit = (emp) => {
    setEditingSalesObj(emp);
    setSalesObjForm({ neuf_target: emp.neuf_target || 0, occasion_target: emp.occasion_target || 0 });
    setShowSalesObjDialog(true);
  };

  const saveSalesObjective = async () => {
    if (!editingSalesObj) return;
    try {
      await axios.put(`${API}/sales-objectives/${editingSalesObj.employee_id}`, {
        month: selectedMonth,
        neuf_target: salesObjForm.neuf_target,
        occasion_target: salesObjForm.occasion_target,
      });
      toast.success('Objectif vente mis a jour');
      setShowSalesObjDialog(false);
      loadData();
    } catch { toast.error('Erreur'); }
  };

  const tabs = [
    { key: 'dashboard', label: 'Tableau de bord', icon: TrendingUp },
    { key: 'overdue', label: 'En retard', count: relances.stats.overdue_count || 0, icon: AlertTriangle, color: 'text-red-600', tabBg: 'bg-red-600' },
    { key: 'today', label: "Aujourd'hui", count: relances.stats.today_count || 0, icon: Clock, color: 'text-amber-600', tabBg: 'bg-amber-600' },
    { key: 'upcoming', label: 'A venir', count: relances.stats.upcoming_count || 0, icon: Calendar, color: 'text-blue-600', tabBg: 'bg-blue-600' },
    { key: 'sales', label: 'Ventes', icon: ShoppingCart, tabBg: 'bg-violet-600' },
    { key: 'objectives', label: 'Objectifs', icon: Settings },
  ];

  const monthLabel = (() => {
    const [y, m] = selectedMonth.split('-');
    return `${MONTHS_FR[parseInt(m) - 1]} ${y}`;
  })();

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-4" data-testid="relances-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Relances & Performance</h1>
            <p className="text-sm text-slate-500">Suivi des relances et ventes par employe</p>
          </div>
          <Button variant="outline" onClick={loadData} data-testid="refresh-relances">{t('common.refresh')}</Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 flex-wrap" data-testid="relance-tabs">
          {tabs.map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === t.key
                  ? (t.tabBg ? `${t.tabBg} text-white` : 'bg-slate-900 text-white')
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              data-testid={`tab-${t.key}`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
              {t.count !== undefined && (
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                  activeTab === t.key ? 'bg-white/20 text-white' : `${t.color || 'text-slate-600'}`
                }`}>{t.count}</span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-400">{t('common.loading')}</div>
        ) : (
          <>
            {/* Dashboard Tab */}
            {activeTab === 'dashboard' && (
              <div className="space-y-5">
                {/* Summary cards */}
                <div className="grid grid-cols-3 gap-3">
                  <Card className="p-4 rounded-xl border-red-200 bg-red-50/50">
                    <div className="flex items-center gap-2 mb-1">
                      <AlertTriangle className="w-4 h-4 text-red-500" />
                      <span className="text-xs font-semibold text-red-600 uppercase">En retard</span>
                    </div>
                    <p className="text-2xl font-bold text-red-700">{relances.stats.overdue_count || 0}</p>
                  </Card>
                  <Card className="p-4 rounded-xl border-amber-200 bg-amber-50/50">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-4 h-4 text-amber-500" />
                      <span className="text-xs font-semibold text-amber-600 uppercase">{t('common.today')}</span>
                    </div>
                    <p className="text-2xl font-bold text-amber-700">{relances.stats.today_count || 0}</p>
                  </Card>
                  <Card className="p-4 rounded-xl border-blue-200 bg-blue-50/50">
                    <div className="flex items-center gap-2 mb-1">
                      <Calendar className="w-4 h-4 text-blue-500" />
                      <span className="text-xs font-semibold text-blue-600 uppercase">A venir</span>
                    </div>
                    <p className="text-2xl font-bold text-blue-700">{relances.stats.upcoming_count || 0}</p>
                  </Card>
                </div>

                {/* Leaderboard */}
                <div>
                  <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2 mb-3">
                    <Trophy className="w-4 h-4 text-yellow-500" /> Classement relances — cette semaine
                  </h2>
                  <div className="grid gap-2">
                    {leaderboard.map(emp => (
                      <LeaderboardCard key={emp.employee_id} emp={emp} />
                    ))}
                  </div>
                </div>

                {/* Employee performance */}
                <div>
                  <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2 mb-3">
                    <Target className="w-4 h-4" /> Detail par employe
                  </h2>
                  <div className="grid gap-3">
                    {employeeStats.map(emp => (
                      <EmployeeStatsCard key={emp.employee_id} emp={emp} />
                    ))}
                  </div>
                </div>

                {employeeStats.length === 0 && (
                  <Card className="p-8 text-center rounded-xl">
                    <User className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                    <p className="text-sm text-slate-500">Aucun employe reception/vente configure</p>
                  </Card>
                )}
              </div>
            )}

            {/* Overdue / Today / Upcoming tabs */}
            {['overdue', 'today', 'upcoming'].includes(activeTab) && (
              <div className="space-y-3">
                {(relances[activeTab] || []).length === 0 ? (
                  <Card className="p-12 text-center rounded-xl">
                    <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-400" />
                    <p className="text-slate-500 font-medium">
                      {activeTab === 'overdue' ? 'Aucune relance en retard' :
                       activeTab === 'today' ? "Aucune relance prevue aujourd'hui" :
                       'Aucune relance a venir'}
                    </p>
                  </Card>
                ) : (
                  (relances[activeTab] || []).map((entry, idx) => (
                    <FollowUpCard key={entry.interaction_id || idx} entry={entry} isOverdue={activeTab === 'overdue'} navigate={navigate} />
                  ))
                )}
              </div>
            )}

            {/* Sales Tab */}
            {activeTab === 'sales' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                    <ShoppingCart className="w-4 h-4 text-violet-600" /> Objectifs vente — {monthLabel}
                  </h2>
                  <input type="month" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}
                    className="text-sm border rounded-lg px-3 py-1.5 text-slate-700" data-testid="sales-month-picker" />
                </div>

                {salesObjectives.length === 0 ? (
                  <Card className="p-8 text-center rounded-xl">
                    <ShoppingCart className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                    <p className="text-sm text-slate-500">Aucun employe</p>
                  </Card>
                ) : (
                  <div className="grid gap-3">
                    {salesObjectives.map(emp => (
                      <SalesObjectiveCard key={emp.employee_id} emp={emp} onEdit={() => openSalesObjEdit(emp)} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Objectives Tab */}
            {activeTab === 'objectives' && (
              <div className="space-y-4">
                <h2 className="text-sm font-bold text-slate-700 mb-2">Objectifs relances hebdomadaires</h2>
                {objectives.map(obj => (
                  <Card key={obj.employee_id} className="p-4 rounded-xl flex items-center justify-between" data-testid={`objective-${obj.employee_id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                        <User className="w-5 h-5 text-slate-500" />
                      </div>
                      <div>
                        <p className="font-semibold text-sm text-slate-800">{obj.employee_name}</p>
                        <p className="text-xs text-slate-500 capitalize">{obj.role}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-lg font-bold text-slate-900">{obj.weekly_target}</p>
                        <p className="text-[10px] text-slate-400 uppercase">relances / semaine</p>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => openObjectiveEdit(obj)} data-testid={`edit-objective-${obj.employee_id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                    </div>
                  </Card>
                ))}

                <h2 className="text-sm font-bold text-slate-700 mt-6 mb-2 flex items-center gap-2">
                  <ShoppingCart className="w-4 h-4 text-violet-600" /> Objectifs vente mensuels — {monthLabel}
                </h2>
                <div className="flex items-center gap-2 mb-2">
                  <input type="month" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}
                    className="text-sm border rounded-lg px-3 py-1.5 text-slate-700" />
                </div>
                {salesObjectives.map(emp => (
                  <Card key={emp.employee_id} className="p-4 rounded-xl flex items-center justify-between" data-testid={`sales-obj-${emp.employee_id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center">
                        <ShoppingCart className="w-5 h-5 text-violet-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-sm text-slate-800">{emp.employee_name}</p>
                        <p className="text-xs text-slate-500 capitalize">{emp.role}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <p className="text-lg font-bold text-blue-700">{emp.neuf_target}</p>
                        <p className="text-[10px] text-blue-500 uppercase">Neufs</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-emerald-700">{emp.occasion_target}</p>
                        <p className="text-[10px] text-emerald-500 uppercase">Occasions</p>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => openSalesObjEdit(emp)} data-testid={`edit-sales-obj-${emp.employee_id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}

        {/* Relance Objective Edit Dialog */}
        <Dialog open={showObjectiveDialog} onOpenChange={setShowObjectiveDialog}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Objectif relances</DialogTitle>
            </DialogHeader>
            {editingObjective && (
              <div className="space-y-4">
                <p className="text-sm text-slate-600">
                  Employe: <strong>{editingObjective.employee_name}</strong>
                </p>
                <div className="space-y-1">
                  <Label className="text-xs">Relances par semaine</Label>
                  <Input type="number" min="0" value={objectiveValue}
                    onChange={(e) => setObjectiveValue(parseInt(e.target.value) || 0)}
                    data-testid="objective-input" />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setShowObjectiveDialog(false)}>{t('common.cancel')}</Button>
                  <Button onClick={saveObjective} data-testid="save-objective-btn">{t('common.save')}</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Sales Objective Edit Dialog */}
        <Dialog open={showSalesObjDialog} onOpenChange={setShowSalesObjDialog}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Objectif ventes — {monthLabel}</DialogTitle>
            </DialogHeader>
            {editingSalesObj && (
              <div className="space-y-4">
                <p className="text-sm text-slate-600">
                  Employe: <strong>{editingSalesObj.employee_name}</strong>
                </p>
                <div className="space-y-1">
                  <Label className="text-xs">Vehicules neufs / mois</Label>
                  <Input type="number" min="0" value={salesObjForm.neuf_target}
                    onChange={(e) => setSalesObjForm(f => ({ ...f, neuf_target: parseInt(e.target.value) || 0 }))}
                    data-testid="neuf-target-input" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Vehicules occasion / mois</Label>
                  <Input type="number" min="0" value={salesObjForm.occasion_target}
                    onChange={(e) => setSalesObjForm(f => ({ ...f, occasion_target: parseInt(e.target.value) || 0 }))}
                    data-testid="occasion-target-input" />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setShowSalesObjDialog(false)}>{t('common.cancel')}</Button>
                  <Button onClick={saveSalesObjective} data-testid="save-sales-obj-btn">{t('common.save')}</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

/* ─── Leaderboard Card ─── */
function LeaderboardCard({ emp }) {
  const rankIcon = emp.rank === 1 ? <Crown className="w-5 h-5 text-yellow-500" /> :
                   emp.rank === 2 ? <Medal className="w-5 h-5 text-slate-400" /> :
                   emp.rank === 3 ? <Medal className="w-5 h-5 text-amber-600" /> : null;
  const bg = emp.rank === 1 ? 'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-300' :
             emp.rank === 2 ? 'bg-slate-50 border-slate-300' :
             emp.rank === 3 ? 'bg-orange-50/50 border-orange-200' : '';

  return (
    <Card className={`p-3 rounded-xl flex items-center gap-4 ${bg}`} data-testid={`leaderboard-${emp.employee_id}`}>
      <div className="w-10 h-10 rounded-full bg-white border-2 border-slate-200 flex items-center justify-center font-bold text-lg text-slate-700 shrink-0">
        {rankIcon || <span className="text-sm text-slate-400">#{emp.rank}</span>}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm text-slate-800">{emp.employee_name}</p>
        <p className="text-xs text-slate-500 capitalize">{emp.role}</p>
      </div>
      <div className="flex items-center gap-3">
        <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
          <div className={`h-full rounded-full ${emp.percentage >= 100 ? 'bg-emerald-500' : emp.percentage >= 50 ? 'bg-amber-500' : 'bg-red-400'}`}
            style={{ width: `${emp.percentage}%` }} />
        </div>
        <div className="text-right min-w-[60px]">
          <span className="text-lg font-bold text-slate-900">{emp.week_count}</span>
          <span className="text-xs text-slate-400"> / {emp.weekly_target}</span>
        </div>
      </div>
    </Card>
  );
}

/* ─── Employee Stats Card ─── */
function EmployeeStatsCard({ emp }) {
  const pct = emp.weekly_target > 0 ? Math.min(100, Math.round((emp.week_count / emp.weekly_target) * 100)) : 0;
  const isOnTrack = emp.status === 'on_track';
  const isBehind = emp.status === 'behind';

  return (
    <Card className={`p-4 rounded-xl ${isBehind ? 'border-red-200 bg-red-50/30' : isOnTrack ? 'border-emerald-200 bg-emerald-50/30' : ''}`} data-testid={`stats-${emp.employee_id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isBehind ? 'bg-red-100' : isOnTrack ? 'bg-emerald-100' : 'bg-slate-100'}`}>
            <User className={`w-5 h-5 ${isBehind ? 'text-red-600' : isOnTrack ? 'text-emerald-600' : 'text-slate-500'}`} />
          </div>
          <div>
            <p className="font-semibold text-sm text-slate-800">{emp.employee_name}</p>
            <p className="text-xs text-slate-500 capitalize">{emp.role}</p>
          </div>
        </div>
        <div className="text-right">
          <div className="flex items-baseline gap-1">
            <span className={`text-2xl font-bold ${isBehind ? 'text-red-700' : isOnTrack ? 'text-emerald-700' : 'text-slate-900'}`}>{emp.week_count}</span>
            <span className="text-sm text-slate-400">/ {emp.weekly_target}</span>
          </div>
          <Badge className={`text-[10px] mt-0.5 ${
            isBehind ? 'bg-red-100 text-red-700' :
            isOnTrack ? 'bg-emerald-100 text-emerald-700' :
            'bg-amber-100 text-amber-700'
          }`}>
            {isBehind ? 'En retard' : isOnTrack ? 'Objectif atteint' : 'En cours'}
          </Badge>
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full h-2.5 bg-slate-200 rounded-full overflow-hidden mb-2">
        <div className={`h-full rounded-full transition-all ${
          isBehind ? 'bg-red-500' : isOnTrack ? 'bg-emerald-500' : 'bg-amber-500'
        }`} style={{ width: `${pct}%` }} />
      </div>

      {/* Daily breakdown */}
      <div className="flex gap-1">
        {Object.entries(emp.daily || {}).map(([day, count], i) => (
          <div key={day} className="flex-1 text-center">
            <p className="text-[10px] text-slate-400">{DAYS_FR[i] || day.slice(8)}</p>
            <p className={`text-xs font-bold ${count > 0 ? 'text-slate-800' : 'text-slate-300'}`}>{count}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 mt-2 text-xs">
        <span className="text-slate-500">Aujourd'hui:</span>
        <span className="font-bold text-slate-800">{emp.today_count} relance{emp.today_count !== 1 ? 's' : ''}</span>
        {emp.remaining > 0 && (
          <span className="text-slate-400">| Reste {emp.remaining} cette semaine</span>
        )}
      </div>
    </Card>
  );
}

/* ─── Sales Objective Card ─── */
function SalesObjectiveCard({ emp, onEdit }) {
  const hasTarget = emp.total_target > 0;
  const isOnTrack = emp.status === 'on_track';
  const isBehind = emp.status === 'behind';
  const neufPct = emp.neuf_target > 0 ? Math.min(100, Math.round((emp.neuf_actual / emp.neuf_target) * 100)) : 0;
  const occPct = emp.occasion_target > 0 ? Math.min(100, Math.round((emp.occasion_actual / emp.occasion_target) * 100)) : 0;

  return (
    <Card className={`p-4 rounded-xl ${isBehind ? 'border-red-200 bg-red-50/30' : isOnTrack ? 'border-emerald-200 bg-emerald-50/30' : ''}`}
      data-testid={`sales-card-${emp.employee_id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isOnTrack ? 'bg-emerald-100' : isBehind ? 'bg-red-100' : 'bg-violet-100'}`}>
            <ShoppingCart className={`w-5 h-5 ${isOnTrack ? 'text-emerald-600' : isBehind ? 'text-red-600' : 'text-violet-600'}`} />
          </div>
          <div>
            <p className="font-semibold text-sm text-slate-800">{emp.employee_name}</p>
            <p className="text-xs text-slate-500 capitalize">{emp.role}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <span className="text-2xl font-bold text-slate-900">{emp.total_actual}</span>
            <span className="text-sm text-slate-400"> / {emp.total_target}</span>
            {hasTarget && (
              <Badge className={`text-[10px] block mt-0.5 ${isOnTrack ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                {isOnTrack ? 'Objectif atteint' : 'En cours'}
              </Badge>
            )}
            {!hasTarget && <p className="text-[10px] text-slate-400 mt-0.5">Pas d'objectif</p>}
          </div>
          <Button size="sm" variant="outline" onClick={onEdit} data-testid={`edit-sales-${emp.employee_id}`}>
            <Pencil className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Neuf + Occasion breakdown */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-blue-50 p-3 rounded-lg">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-semibold text-blue-700">Neufs</span>
            <span className="text-sm font-bold text-blue-900">{emp.neuf_actual} / {emp.neuf_target}</span>
          </div>
          <div className="w-full h-2 bg-blue-200 rounded-full overflow-hidden">
            <div className="h-full bg-blue-600 rounded-full" style={{ width: `${neufPct}%` }} />
          </div>
        </div>
        <div className="bg-emerald-50 p-3 rounded-lg">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-semibold text-emerald-700">Occasions</span>
            <span className="text-sm font-bold text-emerald-900">{emp.occasion_actual} / {emp.occasion_target}</span>
          </div>
          <div className="w-full h-2 bg-emerald-200 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-600 rounded-full" style={{ width: `${occPct}%` }} />
          </div>
        </div>
      </div>
    </Card>
  );
}

/* ─── Follow-Up Card ─── */
function FollowUpCard({ entry, isOverdue, navigate }) {
  const sal = entry.salutation === 'madame' ? 'Mme' : (entry.salutation === 'monsieur' ? 'M.' : '');
  const typeLabel = INTERACTION_TYPE_LABELS[entry.interaction_type] || entry.interaction_type;

  return (
    <Card className={`p-4 rounded-xl transition-shadow cursor-pointer ${isOverdue ? 'border-red-300 bg-red-50/50 hover:shadow-red-100' : 'hover:shadow-md'}`}
      onClick={() => navigate(`/admin/clients/${entry.id}`)}
      data-testid={`followup-card-${entry.interaction_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {isOverdue && <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />}
            <span className={`font-semibold text-sm ${isOverdue ? 'text-red-900' : 'text-slate-900'}`}>
              {sal} {entry.first_name} {entry.last_name}
            </span>
            {entry.company_name && <Badge className="bg-blue-100 text-blue-700 text-[10px]">{entry.company_name}</Badge>}
            {typeLabel && <Badge className="bg-slate-100 text-slate-600 text-[10px]">{typeLabel}</Badge>}
          </div>

          <div className="flex items-center gap-3 text-xs text-slate-500">
            {entry.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{entry.phone}</span>}
            {entry.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{entry.email}</span>}
          </div>

          {entry.vehicles && entry.vehicles.length > 0 && (
            <div className="flex items-center gap-2 mt-1.5">
              {entry.vehicles.slice(0, 2).map(v => (
                <span key={v.id} className="inline-flex items-center gap-1 text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-md">
                  <Car className="w-3 h-3" />
                  {v.brand} {v.model} — {v.plate}
                </span>
              ))}
            </div>
          )}

          <div className={`flex items-center gap-2 mt-1.5 text-xs ${isOverdue ? 'text-red-600 font-medium' : 'text-violet-600'}`}>
            <Calendar className="w-3 h-3" />
            {isOverdue ? `En retard depuis le ${entry.follow_up_date}` : `Relance: ${entry.follow_up_date}`}
            {entry.follow_up_action && <span className="text-slate-500">— {entry.follow_up_action}</span>}
          </div>

          {entry.interaction_notes && (
            <p className="text-xs text-slate-400 mt-1 truncate">{entry.interaction_notes}</p>
          )}

          {entry.interaction_created_by && (
            <p className="text-[10px] text-slate-400 mt-0.5">par {entry.interaction_created_by}</p>
          )}
        </div>
        <ChevronRight className="w-5 h-5 text-slate-300 shrink-0 mt-1" />
      </div>
    </Card>
  );
}

export default RelancesPage;
